import { Component, Input, OnInit, ViewChild } from "@angular/core";
import { Router } from "@angular/router";
import { ClientRateViewModel } from "@app/shared/models/rates/ClientRateViewModel.model";
import { SelectItem } from "primeng/api";
import { Table } from "primeng/table";
import { clientService } from "@app/client/shared/client.service";
import { ProductService } from "@app/product/shared/products.service";
import { LocationsService } from "@app/locations/shared/locations.service";
import { PcMilerService } from "@app/pcMiler/shared/pcMiler.service";
import { ToastrService } from "ngx-toastr";
import { RatesService } from "@app/rates/shared/rates.service";
import { RateCalculatorService } from "@app/trip-new/shared/rate-calculator.service";
import { TripOptions } from "@app/shared/models/trips/TripOptions.model";

@Component({
  selector: "app-quote-builder",
  templateUrl: "./quote-builder.component.html",
  styleUrls: ["./quote-builder.component.css"],
})
export class QuoteBuilderComponent implements OnInit {
  clients: any[] = [];
  selectedClient: any = null;
  billTos: any[] = [];
  filteredBillTos: any[] = [];
  selectedBillTo: any = null;
  products: any[] = [];
  selectedProduct: any = null;

  // Commodity radio selection
  commodityType: string = 'NH3/LPG';

  // For pickup and dropoff location-pickers
  pickupTripData: any = {
    locationId: null,
    locationName: null
  };
  dropoffTripData: any = {
    locationId: null,
    locationName: null
  };

  pickupLocationInputMode: "dropdown" | "manual" = "dropdown";
  dropoffLocationInputMode: "dropdown" | "manual" = "dropdown";

  // Quote result for table display
  quoteResult: any = null;

  // Quote name
  quoteName: string = '';

  private quoteBuilderSelectedQuoteKey = "quoteBuilderSelectedQuote";
  private loadedQuoteForEdit: any = null;
  private editingQuoteId: string | null = null;

  constructor(
    private router: Router,
    private clientService: clientService,
    private productService: ProductService,
    private locationsService: LocationsService,
    private pcMilerService: PcMilerService,
    private toastr: ToastrService,
    private ratesService: RatesService,
    private rateCalculator: RateCalculatorService
  ) {}

  ngOnInit(): void {
    this.hydrateQuoteForEdit();

    this.clientService.getClientList(true, false).subscribe((clients) => {
      this.clients = clients;
      this.applyLoadedSelections();
    });
    this.clientService.listAllBillTo().subscribe((billTos) => {
      this.billTos = billTos;
      this.filteredBillTos = [];
      this.applyLoadedSelections();
    });
    this.productService.listDropDown().subscribe((products) => {
      this.products = products;
      this.applyLoadedSelections();
    });

    this.pcMilerService.getCurrentSetup();
  }

  onClientSelected(event: any) {
    // Filter billTos by selected client
    if (this.selectedClient && this.selectedClient.id) {
      this.filteredBillTos = this.billTos.filter(billTo => billTo.clientId === this.selectedClient.id);
    } else {
      this.filteredBillTos = [];
    }
    this.selectedBillTo = null;
  }

  onPickupLocationSelected(location: any) {
    this.pickupTripData.locationId = location?.id || location?.locId;
    this.pickupTripData.locationName = location?.name;
    this.pickupTripData.city = location?.city;
    this.pickupTripData.zipcode = location?.zipcode || location?.zip;
    this.pickupTripData.latitude = location?.latitude;
    this.pickupTripData.longitude = location?.longitude;
    this.pickupTripData.state = location?.state;
  }

  onDropoffLocationSelected(location: any) {
    this.dropoffTripData.locationId = location?.id || location?.locId;
    this.dropoffTripData.locationName = location?.name;
    this.dropoffTripData.city = location?.city;
    this.dropoffTripData.zipcode = location?.zipcode || location?.zip;
    this.dropoffTripData.latitude = location?.latitude;
    this.dropoffTripData.longitude = location?.longitude;
    this.dropoffTripData.state = location?.state;
  }

  onPickupLocationModeChange() {
    if (this.pickupLocationInputMode === "manual") {
      this.pickupTripData.locationId = null;
      this.pickupTripData.locationName = null;
      this.pickupTripData.latitude = null;
      this.pickupTripData.longitude = null;
    }
  }

  onDropoffLocationModeChange() {
    if (this.dropoffLocationInputMode === "manual") {
      this.dropoffTripData.locationId = null;
      this.dropoffTripData.locationName = null;
      this.dropoffTripData.latitude = null;
      this.dropoffTripData.longitude = null;
    }
  }

  private composeManualLocationName(location: any): string {
    const city = (location?.city || "").trim();
    const state = (location?.state || "").trim();
    const zipcode = (location?.zipcode || "").trim();
    return [city, state, zipcode].filter(Boolean).join(", ");
  }

  private normalizeManualLocation(location: any): any {
    if (!location) return {};
    location.city = (location.city || "").toString().trim();
    location.state = (location.state || location.stateAbbreviation || "").toString().trim();
    location.zipcode = (location.zipcode || location.zip || "").toString().trim();
    location.country = (location.country || "US").toString().trim() || "US";
    return location;
  }

  private extractCoordinatesFromGeocodeResults(geocodeResults: any): { latitude: number; longitude: number } | null {
    const candidates = Array.isArray(geocodeResults)
      ? geocodeResults
      : geocodeResults?.Locations || geocodeResults?.results || geocodeResults?.Results || [];

    if (!Array.isArray(candidates) || !candidates.length) return null;

    const selectedMatch = candidates[0] || {};
    const latRaw =
      selectedMatch?.Coords?.Lat ??
      selectedMatch?.Coords?.Latitude ??
      selectedMatch?.Latitude ??
      selectedMatch?.lat;
    const lonRaw =
      selectedMatch?.Coords?.Lon ??
      selectedMatch?.Coords?.Lng ??
      selectedMatch?.Coords?.Longitude ??
      selectedMatch?.Longitude ??
      selectedMatch?.lng ??
      selectedMatch?.lon;

    const latitude = Number(latRaw);
    const longitude = Number(lonRaw);
    if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return null;

    return { latitude, longitude };
  }

  private hasValidCoordinates(location: any): boolean {
    const latRaw = location?.latitude;
    const lonRaw = location?.longitude;

    const hasLat = latRaw !== null && latRaw !== undefined && latRaw !== "";
    const hasLon = lonRaw !== null && lonRaw !== undefined && lonRaw !== "";
    if (!hasLat || !hasLon) return false;

    const latitude = Number(latRaw);
    const longitude = Number(lonRaw);
    return Number.isFinite(latitude) && Number.isFinite(longitude);
  }

  private async ensureManualLocationCoordinates(location: any, locationLabel: string): Promise<boolean> {
    this.normalizeManualLocation(location);

    const hasCoordinates = this.hasValidCoordinates(location);
    if (hasCoordinates) return true;

    try {
      const geocodeResults: any = await this.pcMilerService.geoCodeLocation({
        city: location?.city,
        state: location?.state,
        zipcode: location?.zipcode,
        zip: location?.zipcode,
        address: location?.address || "",
        country: location?.country || "US",
      });

      const coords = this.extractCoordinatesFromGeocodeResults(geocodeResults);
      if (!coords) {
        this.toastr.error(
          `Unable to geocode ${locationLabel} location. Please verify city/state/zip.`,
          "Geocode Error",
          { closeButton: true }
        );
        return false;
      }

      location.latitude = coords.latitude;
      location.longitude = coords.longitude;
      return true;
    } catch (error: any) {
      const responseText = error?.responseText || error?.message || "Unknown error";
      this.toastr.error(
        `Unable to geocode ${locationLabel} location: ${responseText}`,
        "Geocode Error",
        { closeButton: true }
      );
      return false;
    }
  }

  async generateQuote() {
    const pickupLocation =
      this.pickupLocationInputMode === "manual"
        ? this.composeManualLocationName(this.pickupTripData)
        : this.pickupTripData.locationName || "";
    const dropoffLocation =
      this.dropoffLocationInputMode === "manual"
        ? this.composeManualLocationName(this.dropoffTripData)
        : this.dropoffTripData.locationName || "";

    if (
      this.pickupLocationInputMode === "manual" &&
      (!this.pickupTripData?.city || !this.pickupTripData?.state || !this.pickupTripData?.zipcode)
    ) {
      this.toastr.warning("Please enter pickup city, state, and zip.", "Missing Pickup Details", {
        closeButton: true,
      });
      return;
    }

    if (
      this.dropoffLocationInputMode === "manual" &&
      (!this.dropoffTripData?.city || !this.dropoffTripData?.state || !this.dropoffTripData?.zipcode)
    ) {
      this.toastr.warning("Please enter dropoff city, state, and zip.", "Missing Dropoff Details", {
        closeButton: true,
      });
      return;
    }

    if (!pickupLocation || !dropoffLocation) {
      this.toastr.warning("Please select both pickup and dropoff locations.", "Missing Locations", { closeButton: true });
      return;
    }

    if (this.pickupLocationInputMode === "manual") {
      const geocodedPickup = await this.ensureManualLocationCoordinates(this.pickupTripData, "pickup");
      if (!geocodedPickup) return;
    }

    if (this.dropoffLocationInputMode === "manual") {
      const geocodedDropoff = await this.ensureManualLocationCoordinates(this.dropoffTripData, "dropoff");
      if (!geocodedDropoff) return;
    }

    const pickupHasCoords = this.hasValidCoordinates(this.pickupTripData);
    const dropoffHasCoords = this.hasValidCoordinates(this.dropoffTripData);

    if (!pickupHasCoords || !dropoffHasCoords) {
      this.toastr.error(
        "Pickup or dropoff coordinates are missing. Please reselect the location or verify city/state/zip.",
        "Missing Coordinates",
        { closeButton: true }
      );
      return;
    }

    if (pickupLocation && dropoffLocation) {
      this.pcMilerService
        .calculateDistanceBetweenStops(this.pickupTripData, this.dropoffTripData)
        .subscribe({
          next: async (miles) => {
            const quoteCalculated = await this.setQuoteResult(pickupLocation, dropoffLocation, miles);
            if (quoteCalculated) {
              this.toastr.success("Mileage calculated successfully!", "Success", { closeButton: true });
            }
          },
          error: (error) => {
            this.toastr.error("Error calculating distance between stops", "Error", {
              closeButton: true,
              enableHtml: true
            });
          },
      });
    }
  }

  saveQuote() {
    if (!this.quoteResult) {
      this.toastr.warning("Generate a quote before saving.", "Missing Quote", {
        closeButton: true,
      });
      return;
    }

    if (!this.quoteName || !this.quoteName.trim()) {
      this.toastr.warning("Please enter a quote name before saving.", "Missing Quote Name", {
        closeButton: true,
      });
      return;
    }

    const quoteToSave = this.buildQuoteSavePayload();

    const saveRequest =
      this.commodityType === "Cryo"
        ? this.ratesService.saveCryoQuote(quoteToSave)
        : this.ratesService.saveNH3Quote(quoteToSave);

    saveRequest.subscribe({
      next: () => {
        this.toastr.success("Quote saved successfully!", "Success", { closeButton: true });
      },
      error: () => {
        this.toastr.error("Unable to save quote.", "Error", { closeButton: true });
      },
    });
  }

  async copyQuoteToClipboard() {
    if (!this.quoteResult) {
      this.toastr.warning("Generate a quote before copying.", "Missing Quote", {
        closeButton: true,
      });
      return;
    }

    const quoteText = this.buildClipboardQuoteText();

    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(quoteText);
      } else {
        this.copyTextWithFallback(quoteText);
      }
      this.toastr.success("Quote copied to clipboard.", "Success", { closeButton: true });
    } catch (error) {
      this.toastr.error("Unable to copy quote to clipboard.", "Error", { closeButton: true });
    }
  }

  private buildClipboardQuoteText(): string {
    const lines: string[] = [];
    lines.push(`Quote Name: ${this.quoteName || "(Unnamed Quote)"}`);
    lines.push(`Commodity: ${this.commodityType}`);
    lines.push(`Pickup: ${this.quoteResult?.pickupLocation || ""}`);
    lines.push(`Dropoff: ${this.quoteResult?.dropoffLocation || ""}`);
    lines.push(`Mileage: ${this.quoteResult?.mileage ?? 0}`);

    if (this.commodityType === "Cryo") {
      lines.push(`Linehaul Rate: ${this.quoteResult?.linehaulRate ?? 0}`);
      lines.push(`Linehaul Total: ${this.quoteResult?.linehaulTotal ?? 0}`);
      lines.push(`Loading Miles: ${this.quoteResult?.loadingMiles ?? 0}`);
      lines.push(`Loading Rate: ${this.quoteResult?.loadingRate ?? 0}`);
      lines.push(`Loading Total: ${this.quoteResult?.loadingTotal ?? 0}`);
      lines.push(`Unloading Miles: ${this.quoteResult?.unloadingMiles ?? 0}`);
      lines.push(`Unloading Rate: ${this.quoteResult?.unloadingRate ?? 0}`);
      lines.push(`Unloading Total: ${this.quoteResult?.unloadingTotal ?? 0}`);
      lines.push(`Stop Miles: ${this.quoteResult?.stopMiles ?? 0}`);
      lines.push(`Stop Rate: ${this.quoteResult?.stopRate ?? 0}`);
      lines.push(`Stop Total: ${this.quoteResult?.stopTotal ?? 0}`);
      lines.push(`Subtotal: ${this.quoteResult?.subtotal ?? 0}`);
      lines.push(`FSC Miles: ${this.quoteResult?.fscMiles ?? 0}`);
      lines.push(`FSC Rate: ${this.quoteResult?.fscRate ?? 0}`);
      lines.push(`FSC Total: ${this.quoteResult?.fscTotal ?? 0}`);
      lines.push(`Insurance Miles: ${this.quoteResult?.insuranceMiles ?? 0}`);
      lines.push(`Insurance Rate: ${this.quoteResult?.insuranceRate ?? 0}`);
      lines.push(`Insurance Total: ${this.quoteResult?.insuranceTotal ?? 0}`);
      lines.push(`Invoice Total: ${this.quoteResult?.total ?? 0}`);
    } else {
      lines.push(`Base Rate: ${this.quoteResult?.baseRate ?? 0}`);
      lines.push(`FSC per Mile: ${this.quoteResult?.fscPerMile ?? 0}`);
      lines.push(`Total FSC: ${this.quoteResult?.totalFsc ?? 0}`);
      lines.push(`All In Rate: ${this.quoteResult?.allInRate ?? 0}`);
    }

    return lines.join("\n");
  }

  private copyTextWithFallback(text: string) {
    const textarea = document.createElement("textarea");
    textarea.value = text;
    textarea.style.position = "fixed";
    textarea.style.opacity = "0";
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();
    document.execCommand("copy");
    document.body.removeChild(textarea);
  }

  private buildQuoteSavePayload() {
    if (this.commodityType === "Cryo") {
      const payload: any = {
        quoteName: this.quoteName.trim(),
        clientName: this.selectedClient?.companyName,
        billTo: this.selectedBillTo?.companyName,
        pickupLocationId: this.pickupTripData?.locationId,
        dropoffLocationId: this.dropoffTripData?.locationId,
        linehaulMiles: this.quoteResult?.mileage,
        linehaulRate: this.quoteResult?.linehaulRate,
        linehaulTotal: this.quoteResult?.linehaulTotal,
        loadingMiles: this.quoteResult?.loadingMiles,
        loadingRate: this.quoteResult?.loadingRate,
        loadingTotal: this.quoteResult?.loadingTotal,
        unloadingMiles: this.quoteResult?.unloadingMiles,
        unloadingRate: this.quoteResult?.unloadingRate,
        unloadingTotal: this.quoteResult?.unloadingTotal,
        stopMiles: this.quoteResult?.stopMiles,
        stopRate: this.quoteResult?.stopRate,
        stopTotal: this.quoteResult?.stopTotal,
        subTotal: this.quoteResult?.subtotal,
        fuelSurchargeMiles: this.quoteResult?.fscMiles,
        fuelSurchargeRate: this.quoteResult?.fscRate,
        fuelSurchargeTotal: this.quoteResult?.fscTotal,
        insuranceMiles: this.quoteResult?.insuranceMiles,
        insuranceRate: this.quoteResult?.insuranceRate,
        insuranceTotal: this.quoteResult?.insuranceTotal,
        total: this.quoteResult?.total,
      };
      if (this.editingQuoteId) {
        payload.Id = this.editingQuoteId;
      }
      return payload;
    }

    const payload: any = {
      quoteName: this.quoteName.trim(),
      pickupLocationId: this.pickupTripData?.locationId,
      deliveryLocationId: this.dropoffTripData?.locationId,
      mileage: this.quoteResult?.mileage,
      baseRate: this.quoteResult?.baseRate,
      fscPerMile: this.quoteResult?.fscPerMile,
      totalFSC: this.quoteResult?.totalFsc,
      allInRate: this.quoteResult?.allInRate,
    };
    if (this.editingQuoteId) {
      payload.Id = this.editingQuoteId;
    }
    return payload;
  }

  private hydrateQuoteForEdit() {
    const payloadJson = sessionStorage.getItem(this.quoteBuilderSelectedQuoteKey);
    if (!payloadJson) return;

    sessionStorage.removeItem(this.quoteBuilderSelectedQuoteKey);

    const payload = JSON.parse(payloadJson);
    const quote = payload?.quote || payload;
    if (!quote) return;

    this.loadedQuoteForEdit = quote;
    this.editingQuoteId = quote?.id || null;
    this.commodityType = payload?.commodityType || this.getCommodityTypeFromQuote(quote);
    this.quoteName = this.pick(quote, ["quoteName", "name"]) || "";

    this.locationsService.getLocationById(quote.pickup.id).subscribe((pickupLocation) => {
      this.onPickupLocationSelected(pickupLocation);
      this.locationsService.getLocationById(quote.dropoff.id).subscribe((dropoffLocation) => {
        this.onDropoffLocationSelected(dropoffLocation);
      });
    });

    if (this.commodityType === "Cryo") {
      this.quoteResult = {
        pickupLocation: this.pickupTripData.locationName || "",
        dropoffLocation: this.dropoffTripData.locationName || "",
        mileage: this.toNumber(this.pick(quote, ["linehaulMiles", "mileage"])),
        linehaulRate: this.toNumber(this.pick(quote, ["linehaulRate"])),
        linehaulTotal: this.toNumber(this.pick(quote, ["linehaulTotal", "linehaulAmount"])),
        loadingMiles: this.toNumber(this.pick(quote, ["loadingMiles"])),
        loadingRate: this.toNumber(this.pick(quote, ["loadingRate"])),
        loadingTotal: this.toNumber(this.pick(quote, ["loadingTotal", "loadingAmount"])),
        unloadingMiles: this.toNumber(this.pick(quote, ["unloadingMiles"])),
        unloadingRate: this.toNumber(this.pick(quote, ["unloadingRate"])),
        unloadingTotal: this.toNumber(this.pick(quote, ["unloadingTotal", "unloadingAmount"])),
        stopMiles: this.toNumber(this.pick(quote, ["stopMiles"])),
        stopRate: this.toNumber(this.pick(quote, ["stopRate"])),
        stopTotal: this.toNumber(this.pick(quote, ["stopTotal", "stopAmount"])),
        subtotal: this.toNumber(this.pick(quote, ["subTotal", "subtotal"])),
        fscMiles: this.toNumber(this.pick(quote, ["fuelSurchargeMiles", "fscMiles"])),
        fscRate: this.toNumber(this.pick(quote, ["fuelSurchargeRate", "fscRate"])),
        fscTotal: this.toNumber(this.pick(quote, ["fuelSurchargeTotal", "fscTotal", "fscAmount"])),
        insuranceMiles: this.toNumber(this.pick(quote, ["insuranceMiles"])),
        insuranceRate: this.toNumber(this.pick(quote, ["insuranceRate"])),
        insuranceTotal: this.toNumber(this.pick(quote, ["insuranceTotal", "insuranceAmount"])),
        total: this.toNumber(this.pick(quote, ["total"])),
      };
    } else {
      this.quoteResult = {
        pickupLocation: this.pickupTripData.locationName || "",
        dropoffLocation: this.dropoffTripData.locationName || "",
        mileage: this.toNumber(this.pick(quote, ["mileage"])),
        baseRate: this.toNumber(this.pick(quote, ["baseRate"])),
        fscPerMile: this.toNumber(this.pick(quote, ["fscPerMile", "FSCPerMile"])),
        totalFsc: this.toNumber(this.pick(quote, ["totalFSC", "totalFsc"])),
        allInRate: this.toNumber(this.pick(quote, ["allInRate", "AllInRate"])),
      };
    }
  }

  private applyLoadedSelections() {
    if (!this.loadedQuoteForEdit) return;
    const quote = this.loadedQuoteForEdit;

    if (this.clients?.length) {
      this.selectedClient =
        this.clients.find((c) => c.id === quote?.clientId) ||
        this.clients.find((c) => c.companyName === quote?.clientName) ||
        this.selectedClient;
    }

    if (this.billTos?.length) {
      this.filteredBillTos = this.selectedClient?.id
        ? this.billTos.filter((b) => b.clientId === this.selectedClient.id)
        : this.billTos;

      this.selectedBillTo =
        this.filteredBillTos.find((b) => b.id === quote?.billToId) ||
        this.filteredBillTos.find((b) => b.companyName === quote?.billTo) ||
        this.selectedBillTo;
    }

    if (this.products?.length) {
      this.selectedProduct =
        this.products.find((p) => p.id === quote?.productId) ||
        this.products.find((p) => p.name === quote?.productName || p.itemName === quote?.productName) ||
        this.selectedProduct;
    }
  }

  private pick(source: any, keys: string[]) {
    for (const key of keys) {
      if (source && source[key] !== undefined && source[key] !== null) return source[key];
    }
    return null;
  }

  private toNumber(value: any): number {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  private getCommodityTypeFromQuote(quote: any): string {
    if (
      quote?.linehaulMiles !== undefined ||
      quote?.linehaulTotal !== undefined ||
      quote?.fuelSurchargeTotal !== undefined
    ) {
      return "Cryo";
    }
    return "NH3/LPG";
  }

  async setQuoteResult(pickupLocation: string, dropoffLocation: string, mileage: number): Promise<boolean> {
    // Build TripOptions for the calculator
    const now = new Date();
    const quoteTripStops: any[] = [
      {
        location: null,
        locId: this.pickupTripData?.locId || "",
        locationId: this.pickupTripData?.locationId || "",
        name: pickupLocation,
        address: this.pickupTripData?.address || "",
        address2: this.pickupTripData?.address2 || "",
        city: this.pickupTripData?.city || "",
        state: this.pickupTripData?.state || "",
        zipcode: this.pickupTripData?.zipcode || "",
        latitude: this.pickupTripData?.latitude || 0,
        longitude: this.pickupTripData?.longitude || 0,
        country: this.pickupTripData?.country || "US",
        stopType: "LOAD",
        sequence: 1,
        stopTimeMinutes: 0,
        startDate: now,
        endDate: now,
        comments: null,
        internalComments: null,
        sealNo: null,
        plantNumber: null,
        custRefNo: null,
        shipperRefNo: null,
        shipper: this.pickupTripData?.locationId || null,
        consignee: null,
        tankUnloads: [],
        weightOrMeter: false,
        units: "POUNDS",
        gross: null,
        tare: null,
        scfGross: null,
        scfTare: null,
        scfNet: null,
        net: null,
        amount: null,
        startValue: null,
        endValue: null,
        scfAmount: null,
        scfStartValue: null,
        scfEndValue: null,
        miles: 0,
        oneWayMiles: 0,
        softDelete: false,
      },
      {
        location: null,
        locId: this.dropoffTripData?.locId || "",
        locationId: this.dropoffTripData?.locationId || "",
        name: dropoffLocation,
        address: this.dropoffTripData?.address || "",
        address2: this.dropoffTripData?.address2 || "",
        city: this.dropoffTripData?.city || "",
        state: this.dropoffTripData?.state || "",
        zipcode: this.dropoffTripData?.zipcode || "",
        latitude: this.dropoffTripData?.latitude || 0,
        longitude: this.dropoffTripData?.longitude || 0,
        country: this.dropoffTripData?.country || "US",
        stopType: "UNLOAD",
        sequence: 2,
        stopTimeMinutes: 0,
        startDate: now,
        endDate: now,
        comments: null,
        internalComments: null,
        sealNo: null,
        plantNumber: null,
        custRefNo: null,
        shipperRefNo: null,
        shipper: null,
        consignee: this.dropoffTripData?.locationId || null,
        tankUnloads: [],
        weightOrMeter: false,
        units: "POUNDS",
        gross: null,
        tare: null,
        scfGross: null,
        scfTare: null,
        scfNet: null,
        net: null,
        amount: 0,
        startValue: null,
        endValue: null,
        scfAmount: null,
        scfStartValue: null,
        scfEndValue: null,
        miles: mileage,
        oneWayMiles: mileage,
        softDelete: false,
      },
    ];

    const tripData: any = {
      attachedFiles: [],
      billAddressId: 0,
      client: this.selectedClient,
      clientId: this.selectedClient?.id || '',
      driver: null,
      driverId: '',
      productId: this.selectedProduct?.id || '',
      product: this.selectedProduct,
      productName: this.selectedProduct?.name || '',
      tractor: null,
      tractorId: '',
      trailer: null,
      trailerId: '',
      tripDrivers: [],
      tripStops: quoteTripStops,
      beginMiles: 0,
      billTo: this.selectedBillTo?.companyName || '',
      billToId: this.selectedBillTo?.id || '',
      driverName: '',
      endDate: now,
      endLocAddress: '',
      endLocCity: '',
      endLocId: this.dropoffTripData?.locationId || '',
      endLocLatitude: this.dropoffTripData?.latitude || 0,
      endLocLongitude: this.dropoffTripData?.longitude || 0,
      endLocName: dropoffLocation,
      endLocState: '',
      endLocZipcode: '',
      endMiles: 0,
      id: '',
      isTeam: false,
      originLocAddress: '',
      originLocCity: '',
      originLocId: this.pickupTripData?.locationId || '',
      originLocLatitude: this.pickupTripData?.latitude || 0,
      originLocLongitude: this.pickupTripData?.longitude || 0,
      originLocName: pickupLocation,
      originLocState: '',
      originLocZipcode: '',
      startDate: now,
      status: '',
      suggestedRules: [],
      totalMiles: mileage,
      totalOneWayMiles: mileage,
      billableMiles: mileage,
      payableMiles: mileage,
      totalTime: 0,
      tractorNo: '',
      trailerNo: '',
      trailerType: this.selectedProduct?.trailerType || '',
      tripNo: '',
    };
    const pickupStateCode = this.pickupTripData?.state || "";

    const tripOptions: TripOptions = {
      tripData,
      startDate: now,
      endDate: now,
      origin: pickupStateCode,
      destination: pickupStateCode,
      trailerType: this.selectedProduct?.trailerType || 'SHIPPER',
      productName: this.selectedProduct?.name || '',
      billToId: this.selectedBillTo?.id || '',
      isTeam: false,
      miles: mileage,
      totalTime: 0,
      unloadAmounts: [],
      tripStops: quoteTripStops,
    };

    try {
      let charges: any[] = [];
      if (this.commodityType === 'Cryo') {
        charges = await this.rateCalculator.calculateCharges(tripOptions);
        // Map charges to quoteResult fields as needed
        // Example: find linehaul, loading, unloading, stop, fsc, insurance, total, etc.
        const get = (type: string) => charges.find(c => c.chargeType?.name?.toLowerCase().includes(type));
        const isFuelOrInsuranceSurcharge = (charge: any) => {
          const chargeTypeName = charge?.chargeType?.name?.toLowerCase() || "";
          return chargeTypeName.includes("fuel") || chargeTypeName.includes("inssurcharge");
        };
        this.quoteResult = {
          pickupLocation,
          dropoffLocation,
          mileage,
          linehaulRate: get('linehaul')?.chargeRate || 0,
          linehaulTotal: get('linehaul')?.price || 0,
          loadingMiles: get('loading')?.miles || 0,
          loadingRate: get('loading')?.chargeRate || 0,
          loadingTotal: get('loading')?.price || 0,
          unloadingMiles: get('unloading')?.miles || 0,
          unloadingRate: get('unloading')?.chargeRate || 0,
          unloadingTotal: get('unloading')?.price || 0,
          stopMiles: get('stop')?.miles || 0,
          stopRate: get('stop')?.chargeRate || 0,
          stopTotal: get('stop')?.price || 0,
          subtotal: charges
            .filter((c) => !isFuelOrInsuranceSurcharge(c))
            .reduce((sum, c) => sum + (c.price || 0), 0),
          fscMiles: get('fuel')?.miles || 0,
          fscRate: get('fuel')?.chargeRate || 0,
          fscTotal: get('fuel')?.price || 0,
          insuranceMiles: get('inssurcharge')?.units || 0,
          insuranceRate: get('inssurcharge')?.chargeRate || 0,
          insuranceTotal: get('inssurcharge')?.price || 0,
          total: charges.reduce((sum, c) => sum + (c.price || 0), 0),
        };
      } else {
        charges = await this.rateCalculator.calculateCharges(tripOptions);
        const isLinehaulCharge = (charge: any) => {
          const chargeTypeName = charge?.chargeType?.name?.toLowerCase() || "";
          const rateName = charge?.rateName?.toLowerCase() || "";
          return chargeTypeName.includes("linehaul") || rateName.includes("linehaul");
        };

        this.quoteResult = {
          pickupLocation,
          dropoffLocation,
          mileage,
          baseRate: charges
            .filter((c) => isLinehaulCharge(c))
            .reduce((sum, c) => sum + (c.price || 0), 0),
          fscPerMile: charges.find(c => c.rateName?.toLowerCase().includes('fuel'))?.chargeRate || 0,
          totalFsc: charges.find(c => c.rateName?.toLowerCase().includes('fuel'))?.price || 0,
          allInRate: charges.reduce((sum, c) => sum + (c.price || 0), 0),
        };
      }
      return true;
    } catch (err: any) {
      this.toastr.error('Error calculating quote: ' + (err && err.message ? err.message : err), 'Calculation Error', { closeButton: true });
      this.quoteResult = null;
      return false;
    }
  }
}
